import type { AvailableFreightMode } from "api/response/location.response";

export type FreightModeCatalogItem = Pick<AvailableFreightMode, "label" | "value" | "type"> & {
  options?: AvailableFreightMode["options"];
};

export type FreightModeIconLookup = {
  value: string;
  type?: string;
};

const STANDARD_LABEL_MAP: Record<string, FreightModeIconLookup> = {
  "on board courier (obc)": { value: "OBC", type: "air" },
  "next flight out (nfo)": { value: "NFO", type: "air" },
  "regular air freight": { value: "regular_air", type: "air" },
  "air freight": { value: "regular_air", type: "air" },
  "air charter": { value: "air_charter", type: "air" },
  "ocean freight": { value: "ocean_freight", type: "ocean" },
  "ocean lcl": { value: "ocean_freight", type: "ocean" },
  parcel: { value: "parcel", type: "parcel" },
  "rail freight": { value: "rail_freight", type: "rail" },
};

const PAREN_CODE_TYPES: Record<string, string> = {
  OBC: "air",
  NFO: "air",
};

const normalizeLabel = (label: string) => label.trim().toLowerCase().replace(/\s+/g, " ");

const toModeKey = (modeName: string) =>
  modeName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const inferTypeFromLabel = (label: string): string | undefined => {
  const lower = label.toLowerCase();
  if (/\bocean\b/.test(lower) || /\blcl\b/.test(lower) || /\bfcl\b/.test(lower)) {
    return "ocean";
  }
  if (/\brail\b/.test(lower)) {
    return "rail";
  }
  if (/\broad\b/.test(lower) || /\btruck\b/.test(lower) || /\bftl\b/.test(lower) || /\bltl\b/.test(lower)) {
    return "road";
  }
  if (/\bair\b/.test(lower) || /\bobc\b/.test(lower) || /\bnfo\b/.test(lower) || /\bcharter\b/.test(lower)) {
    return "air";
  }
  return undefined;
};

const matchCatalog = (
  modeName: string,
  availableModes: FreightModeCatalogItem[],
): FreightModeIconLookup | undefined => {
  if (!availableModes.length) {
    return undefined;
  }

  const normalized = normalizeLabel(modeName);
  const exact = availableModes.find((mode) => normalizeLabel(mode.label) === normalized);
  if (exact) {
    return { value: exact.value, type: exact.type };
  }

  const optionMatch = availableModes.find((mode) =>
    (mode.options ?? []).some((option) => {
      const dashLabel = normalizeLabel(`${mode.label} - ${option.label}`);
      const colonLabel = normalizeLabel(`${mode.label}: ${option.label}`);
      return normalized === dashLabel || normalized === colonLabel;
    }),
  );
  if (optionMatch) {
    return { value: optionMatch.value, type: optionMatch.type };
  }

  return undefined;
};

export const resolveFreightModeForIcon = (
  modeName: string,
  availableModes: FreightModeCatalogItem[] = [],
): FreightModeIconLookup => {
  const catalogMatch = matchCatalog(modeName, availableModes);
  if (catalogMatch) {
    return catalogMatch;
  }

  const normalized = normalizeLabel(modeName);
  const standard = STANDARD_LABEL_MAP[normalized];
  if (standard) {
    return standard;
  }

  if (normalized.startsWith("road freight")) {
    return { value: "road_freight", type: "road" };
  }

  const parenMatch = modeName.match(/\(([^)]+)\)\s*$/);
  if (parenMatch?.[1]) {
    const code = parenMatch[1].trim();
    return { value: code, type: PAREN_CODE_TYPES[code] ?? inferTypeFromLabel(modeName) };
  }

  const type = inferTypeFromLabel(modeName);
  return type ? { value: toModeKey(modeName), type } : { value: toModeKey(modeName) };
};
